<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('settings.settingsLabel') }}</span>
        <h3 class="page-title">{{ $t('settings.processingTime.editProcessingTime') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/ustawienia/czas-realizacji')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">{{ $t('ui.saveChanges') }}</d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form v-if="allDataLoaded">
      <d-card class="mb-5">
        <d-tabs v-sticky-tabs card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="processing-time-edit-form" tag="form" v-if="loaded">
              <div class="form-group">
                <label for="name">
                  {{ $t('settings.processingTime.processingTimeName') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </validation-observer>

            <div v-if="!loaded" class="pb-3 pt-3 text-center">
              {{ $t('ui.dataLoading') }}&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
            </d-alert>
          </d-tab>

          <d-tab :title="$t('settings.processingTime.translations')">
            <h4 class="pb-3 mb-0 border-bottom d-flex">
              {{ $t('settings.transaltion.translations') }}

              <d-button
                class="ml-auto"
                theme="success"
                @click.prevent="addTranslation">
                <i class="material-icons">add</i>
                {{ $t('settings.transaltion.addTranslation') }}
              </d-button>
            </h4>

            <table class="table mt-2 mb-0 full-width">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.language') }}</th>
                  <th scope="col" class="border-0" style="width: calc(100% - 350px);">{{ $t('settings.processingTime.processingTimeName') }}</th>
                  <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
                </tr>
              </thead>
              <tbody v-if="translations.length">
                <tr
                  v-for="(translation, index) of translations"
                  :key="'price-' + index">
                  <td class="align-middle">
                    {{ getLanguageByID(translation.language_id) }}
                  </td>
                  <td class="align-middle">
                    {{ translation.name }}
                  </td>
                  <td class="align-middle">
                    <d-button
                      theme="primary"
                      @click.prevent="editTranslation(translation)"
                      class="mr-2"
                      :title="$t('ui.edit')">
                      <i class="material-icons">edit</i>
                    </d-button>
                    <d-button
                      theme="danger"
                      :disabled="removeTranslationInProgress"
                      @click.prevent="removeTranslation(translation.id)"
                      :title="$t('ui.delete')">
                      <i class="material-icons">delete</i>
                    </d-button>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="3" class="text-center">
                    {{ $t('settings.processingTime.noTranslationsForThisProcessingTime') }}&hellip;
                  </td>
                </tr>
              </tbody>
            </table>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>

    <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
    </div>

    <processing-time-translation-new-popup
      v-if="showAddTranslationPopup"
      :processingTimeID="this.id"
      :languages="languages" />

    <processing-time-translation-edit-popup
      v-if="showEditTranslationPopup"
      :processingTimeID="this.id"
      :translationData="selectedTranslation"
      :languages="languages" />
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import ProcessingTimeTranslationNewPopup from './ProcessingTimeTranslationNewPopup';
import ProcessingTimeTranslationEditPopup from './ProcessingTimeTranslationEditPopup';

export default {
  name: 'processing-time-edit',
  components: {
    'processing-time-translation-new-popup': ProcessingTimeTranslationNewPopup,
    'processing-time-translation-edit-popup': ProcessingTimeTranslationEditPopup
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedLanguages && this.loadedTranslations;
    }
  },
  data() {
    return {
      id: 0,
      name: '',
      loaded: false,
      loadError: false,
      languages: [],
      loadedLanguages: false,
      translations: [],
      loadedTranslations: false,
      showAddTranslationPopup: false,
      showEditTranslationPopup: false,
      selectedTranslation: 0,
      removeTranslationInProgress: false
    };
  },
  mounted () {
    FormUtils.loadItemData(this, {
      endpointBase: '/processing-time/item/',
      instanceFields: ['name'],
      loadedKey: 'loaded'
    });

    this.loadLanguages();
    this.loadTranslations();
    this.$bus.$on('processing-time-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$on('processing-time-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$on('processing-time-reload-translations', this.loadTranslations);
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['processing-time-edit-form'], this.submitForm);
    },
    submitForm () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/processing-time/update/' + this.id,
        formData: {
          name: this.name,
        },
        successTitle: this.$t('settings.processingTime.editProcessingTimeSuccessTitle'),
        successText: this.$t('settings.processingTime.editProcessingTimeSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.processingTime.editProcessingTimeErrorText'),
        successAction: () => {
          this.$router.push('/ustawienia/czas-realizacji')
        }
      });
    },
    loadLanguages () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError'
      });
    },
    getLanguageByID (languageID) {
      let filteredLanguages = this.languages.filter(language => language.id === languageID);

      if (filteredLanguages.length) {
        return filteredLanguages[0].name;
      }

      return '';
    },
    loadTranslations () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/processing-time/translations/items',
        loadedKey: 'loadedTranslations',
        outputKey: 'translations',
        errorKey: 'loadError',
        params: {
          processing_time_id: this.id
        }
      });
    },
    addTranslation () {
      this.showAddTranslationPopup = true;
    },
    editTranslation (translationData) {
      this.selectedTranslation = JSON.parse(JSON.stringify(translationData));
      this.showEditTranslationPopup = true;
    },
    closeAddTranslationPopup () {
      this.showAddTranslationPopup = false;
    },
    closeEditTranslationPopup () {
      this.showEditTranslationPopup = false;
    },
    removeTranslation (translationID) {
      this.removeTranslationInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/processing-time/translations/delete/',
        id: translationID,
        successTitle: this.$t('settings.transaltion.deleteTranslationSuccessTitle'),
        successText: this.$t('settings.transaltion.deleteTranslationSuccessText'),
        confirmTitle: this.$t('settings.transaltion.deleteTranslationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.transaltion.deleteTranslationErrorText'),
        successAction: () => {
          this.removeTranslationInProgress = false;
          this.loadTranslations();
        },
        cancelAction: () => {
          this.removeTranslationInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('processing-time-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$off('processing-time-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$off('processing-time-reload-translations', this.loadTranslations);
  }
};
</script>
